import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  Select,
  SvgImage,
  Result,
  MathDiv,
  ActionButtonsRow,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  CopyHTMLButton,
  CopyImageButton,
  ImageDiv,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { FaRegCopy } from 'react-icons/fa6'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function SteelWeldNormal() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [b, setB] = useState('')
  const [l, setL] = useState('')
  const [fw, setFw] = useState(415)
  const [fy, setFy] = useState(250)
  const [nk, setNk] = useState('')
  const [gammaf, setGammaf] = useState(1.4)
  const [gammaw2, setGammaw2] = useState(1.35)
  const [selectedMode, setSelectedMode] = useState('Obter espessura')
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [touched, setTouched] = useState({})
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)
  const inputImageRef = useRef(null)

  const modes = useMemo(() => ['Obter espessura', 'Obter comprimento', 'Obter resistência'], [])

  const HISTORY_KEY = 'steel-weld-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setSelectedMode(entry.selectedMode)
      setFy(entry.fy)
      setFw(entry.fw)
      setB(entry.b)
      setL(entry.l)
      setNk(entry.nk || entry.nd || '')
      setGammaf(entry.gammaf || 1.4)
      setGammaw2(entry.gammaw2)
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback(
    (name, value) => {
      // Campos que devem ser ignorados por modo
      const skipValidationMap = {
        'Obter resistência': ['nk', 'gammaf'],
        'Obter comprimento': ['l'],
        'Obter espessura': ['b'],
      }

      // Se o campo deve ser ignorado neste modo, não valida
      if (skipValidationMap[selectedMode]?.includes(name)) {
        return null
      }

      if (value === '' || value === null || value === undefined) {
        return 'Campo obrigatório'
      }

      const numValue = Number(value)

      if (isNaN(numValue)) {
        return 'Valor deve ser um número'
      }

      if (numValue <= 0) {
        return 'Valor deve ser maior que 0'
      }

      return null
    },
    [selectedMode]
  )

  useEffect(() => {
    const newErrors = {}

    newErrors.fy = validateField('fy', fy)
    newErrors.fw = validateField('fw', fw)
    newErrors.b = validateField('b', b)
    newErrors.l = validateField('l', l)
    newErrors.nk = validateField('nk', nk)
    newErrors.gammaf = validateField('gammaf', gammaf)
    newErrors.gammaw2 = validateField('gammaw2', gammaw2)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [b, l, fw, fy, nk, gammaf, gammaw2, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const nd = nk * gammaf

      const data = {
        b,
        l,
        nd,
        selectedMode,
        gammaw2,
        fw,
        fy,
      }

      const response = await api.post('/steel/weld-normal', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      setData(data)

      saveToHistory({
        selectedMode,
        fy,
        fw,
        b,
        l,
        nk,
        gammaf,
        gammaw2,
        result: response.data,
        data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      b,
      l,
      gammaw2,
      nk,
      gammaf,
      fw,
      fy,
      selectedMode,
      result,
      data,
      isFormValid,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'state.json'
    link.click()
    URL.revokeObjectURL(url)
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setB(jsonData.b)
        setL(jsonData.l)
        setGammaw2(jsonData.gammaw2)
        setFw(jsonData.fw)
        setFy(jsonData.fy)
        setNk(jsonData.nk || jsonData.nd || '')
        setGammaf(jsonData.gammaf || 1.4)
        setSelectedMode(jsonData.selectedMode)
        setResult(jsonData.result)
        setData(jsonData.data)
        setIsFormValid(jsonData.isFormValid)
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
      }
    }
    reader.readAsText(file)
  }

  const handleTypeChange = (event) => {
    const selectedMode = event.target.value
    setSelectedMode(selectedMode)
  }

  const InputImage = () => {
    return (
      <SvgImage ref={inputImageRef} viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="97.5" y="135.5" width="21" height="308" stroke="black" strokeWidth="3" />
        <line x1="119" y1="390.5" x2="374" y2="390.5" stroke="black" strokeWidth="3" />
        <line x1="119" y1="182.5" x2="374" y2="182.5" stroke="black" strokeWidth="3" />
        <line x1="375" y1="165" x2="375" y2="404" stroke="black" strokeWidth="2" />
        <path d="M479 283.5L459 271.953V295.047L479 283.5ZM388 285.5H461V281.5H388V285.5Z" fill="black" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="388" y="265.273">
            {selectedMode == 'Obter resistência' ? 'Nk?' : nk != 0 && nk != '' ? nk : 'Nk'}
          </tspan>
        </text>
        <path
          d="M127.123 294.52L136.988 288.519L126.858 282.976L127.123 294.52ZM132.32 287.105L244 83L242.245 82.04L130.566 286.145L132.32 287.105Z"
          fill="black"
        />
        <line x1="276" y1="83" x2="244" y2="83" stroke="black" strokeWidth="2" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="248" y="74.2727">
            {selectedMode == 'Obter espessura' ? 'b?' : b != 0 && b != '' ? b : 'b'}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="end"
        >
          <tspan x="53.2539" y="294.773">
            {selectedMode == 'Obter comprimento' ? 'l?' : l != 0 && l != '' ? l : 'l'}
          </tspan>
        </text>
        <path
          d="M67 184L61.2265 194L72.7735 194L67 184ZM67 390L72.7735 380L61.2265 380L67 390ZM66 193L66 381L68 381L68 193L66 193Z"
          fill="black"
        />
        <line x1="35" y1="183" x2="86" y2="183" stroke="black" strokeWidth="2" />
        <line x1="35" y1="390" x2="86" y2="390" stroke="black" strokeWidth="2" />
      </SvgImage>
    )
  }

  const handleCopySVG = async () => {
    const svgElement = inputImageRef.current
    if (!svgElement) return

    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()

    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 500
      canvas.height = 500
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #2c3e50 0%, #34495e 100%)">
        <div className="header-content">
          <BackButton to="/aco">
            <FiArrowLeft /> Voltar para Aço
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/aco">Aço</a>
            <span>/</span>
            <span>Soldas</span>
          </div>
          <h1>🔩 Soldas Submetidas a Esforços Axiais</h1>
          <p className="subtitle">Dimensionamento de soldas de filete submetidas a esforços axiais centrados</p>
          <div className="badges">
            <Badge>📋 ABNT NBR 8800:2008</Badge>
            <Badge>🧮 Esforços Axiais</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #e8f4f8 0%, #cfe9f3 100%)"
        $border="2px solid #a8d8ea"
        $primaryColor="#2c3e50"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento de soldas de filete submetidas a esforços axiais centrados,
              conforme a ABNT NBR 8800:2008.
            </p>
            <p>
              O cálculo verifica a resistência do metal base e do metal de solda, permitindo obter a espessura
              necessária, o comprimento mínimo ou a resistência da solda.
            </p>
            <div className="norms">
              <span>ABNT NBR 8800:2008</span>
              <span>Soldas de Filete</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <ImageDiv>
            <InputImage />
            <CopyImageButton title="Clique para copiar essa imagem" onClick={handleCopySVG}>
              <FaRegCopy />
              Copiar
            </CopyImageButton>
          </ImageDiv>
          <InputDiv>
            <span>Modo de cálculo</span>
            <div>
              <Select style={{ marginLeft: '0' }} id="modeSelect" value={selectedMode} onChange={handleTypeChange}>
                {modes.map((mu, index) => (
                  <option key={index} value={mu}>
                    {mu}
                  </option>
                ))}
              </Select>
            </div>
          </InputDiv>
          <h3>Materiais</h3>
          <form onSubmit={handleCalculate}>
            <InputDiv>
              <span>Resistência do metal base (MPa)</span>
              <ValidatedInput
                name="fy"
                type="number"
                value={fy || ''}
                onChange={(event) => setFy(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência do metal de solda (MPa)</span>
              <ValidatedInput
                name="fw"
                type="number"
                value={fw || ''}
                onChange={(event) => setFw(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            {selectedMode != 'Obter comprimento' && (
              <InputDiv>
                <span>Comprimento (mm)</span>
                <ValidatedInput
                  name="l"
                  type="number"
                  value={l || ''}
                  onChange={(event) => setL(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            {selectedMode != 'Obter espessura' && (
              <InputDiv>
                <span>Espessura da solda (mm)</span>
                <ValidatedInput
                  name="b"
                  type="number"
                  value={b || ''}
                  onChange={(event) => setB(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            <h3>Esforços</h3>
            {selectedMode != 'Obter resistência' && (
              <>
                <InputDiv>
                  <span>Esforço Característico (kN)</span>
                  <ValidatedInput
                    name="nk"
                    type="number"
                    value={nk || ''}
                    onChange={(event) => setNk(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Coeficiente de ponderação γf</span>
                  <ValidatedInput
                    name="gammaf"
                    type="number"
                    value={gammaf || ''}
                    onChange={(event) => setGammaf(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            )}
            <h3>Considerações</h3>
            <InputDiv>
              <span>
                &gamma;<sub>w2</sub>
              </span>
              <ValidatedInput
                name="gammaw2"
                type="number"
                value={gammaw2 || ''}
                onChange={(event) => setGammaw2(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              {data.selectedMode === 'Obter espessura' && (
                <MathDiv>
                  <BlockMath math={`b \\geq ${result.bResponse.toFixed(0)} \\, mm`} />
                </MathDiv>
              )}
              {data.selectedMode === 'Obter comprimento' && (
                <MathDiv>
                  <BlockMath math={`L \\geq ${result.lResponse.toFixed(0)} \\, mm`} />
                </MathDiv>
              )}
              {data.selectedMode === 'Obter resistência' && (
                <MathDiv>
                  <BlockMath math={`F_{wRd}= ${result.fwrd.toFixed(2)} \\, kN`} />
                </MathDiv>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              {data.selectedMode === 'Obter espessura' && (
                <>
                  <h3>Verificação do metal base</h3>
                  <MathDiv>
                    <BlockMath math={`F_{wRd1}=0,6 \\cdot A_{MB} \\cdot \\dfrac{f_y}{1,1}  `} />
                    <BlockMath math={`A_{MB}= \\dfrac{1,1 \\cdot N_d}{0,6 \\cdot fy}   `} />
                    <BlockMath math={`A_{MB}= \\dfrac{1,1 \\cdot ${data.nd}}{0,6 \\cdot ${data.fy / 10}}   `} />
                    <BlockMath math={`A_{MB}=${result.amb.toFixed(2)} \\, cm^2  `} />
                    <BlockMath math={`A_{MB}=b_1 \\cdot L`} />
                    <BlockMath
                      math={`b_1 = \\dfrac{A_{MB}}{L}= \\dfrac{${(result.amb * 100).toFixed(2)}}{${data.l}}`}
                    />
                    <BlockMath math={`b_1 \\geq ${result.b1.toFixed(2)} \\, mm`} />
                  </MathDiv>
                  <h3>Verificação do metal de solda</h3>
                  <MathDiv>
                    <BlockMath math={`F_{wRd2}=0,6 \\cdot A_w \\cdot \\dfrac{f_w}{\\gamma_{w2}}  `} />
                    <BlockMath
                      math={`A_w= \\dfrac{N_d \\cdot \\gamma_{w2}}{0,6 \\cdot f_w} = \\dfrac{${data.nd} \\cdot ${data.gammaw2}}{0,6 \\cdot ${data.fw / 10}}`}
                    />
                    <BlockMath math={`A_w= ${result.aw.toFixed(2)} \\, cm^2  `} />
                    <BlockMath math={`A_w = a \\cdot L`} />
                    <BlockMath math={`a = 0,7 \\cdot b_2`} />
                    <BlockMath
                      math={`b_2 = \\dfrac{A_w}{0,7 \\cdot L}=\\dfrac{${(result.aw * 100).toFixed(2)}}{0,7 \\cdot ${data.l}}`}
                    />
                    <BlockMath math={`b_2 \\geq ${result.b2.toFixed(0)} \\, mm`} />
                  </MathDiv>
                  <h3>Conclusão</h3>
                  <MathDiv>
                    <BlockMath math={`\\boxed{b \\geq ${result.bResponse.toFixed(0)} \\, mm}`} />
                  </MathDiv>
                </>
              )}
              {data.selectedMode === 'Obter comprimento' && (
                <>
                  <h3>Verificação do metal base</h3>
                  <MathDiv>
                    <BlockMath math={`F_{wRd1}=0,6 \\cdot A_{MB} \\cdot \\dfrac{f_y}{1,1}  `} />
                    <BlockMath math={`A_{MB}= \\dfrac{1,1 \\cdot N_d}{0,6 \\cdot fy}   `} />
                    <BlockMath math={`A_{MB}= \\dfrac{1,1 \\cdot ${data.nd}}{0,6 \\cdot ${data.fy / 10}}   `} />
                    <BlockMath math={`A_{MB}=${result.amb.toFixed(2)} \\, cm^2  `} />
                    <BlockMath math={`A_{MB}=b \\cdot L_1`} />
                    <BlockMath
                      math={`L_1 = \\dfrac{A_{MB}}{b}= \\dfrac{${(result.amb * 100).toFixed(2)}}{${data.b}}`}
                    />
                    <BlockMath math={`L_1 \\geq ${result.l1.toFixed(0)} \\, mm`} />
                  </MathDiv>
                  <h3>Verificação do metal de solda</h3>
                  <MathDiv>
                    <BlockMath math={`F_{wRd2}=0,6 \\cdot A_w \\cdot \\dfrac{f_w}{\\gamma_{w2}}  `} />
                    <BlockMath
                      math={`A_w= \\dfrac{N_d \\cdot \\gamma_{w2}}{0,6 \\cdot f_w} = \\dfrac{${data.nd} \\cdot ${data.gammaw2}}{0,6 \\cdot ${data.fw / 10}}`}
                    />
                    <BlockMath math={`A_w= ${result.aw.toFixed(2)} \\, cm^2  `} />
                    <BlockMath math={`A_w = a \\cdot L_2`} />
                    <BlockMath math={`a = 0,7 \\cdot b`} />
                    <BlockMath
                      math={`L_2 = \\dfrac{A_w}{0,7 \\cdot b}=\\dfrac{${(result.aw * 100).toFixed(2)}}{0,7 \\cdot ${data.b}}`}
                    />
                    <BlockMath math={`L_2 \\geq ${result.l2.toFixed(0)} \\, mm`} />
                  </MathDiv>
                  <h3>Conclusão</h3>
                  <MathDiv>
                    <BlockMath math={`\\boxed{L \\geq ${result.lResponse.toFixed(0)} \\, mm}`} />
                  </MathDiv>
                </>
              )}
              {data.selectedMode === 'Obter resistência' && (
                <>
                  <h3>Verificação do metal base</h3>
                  <MathDiv>
                    <BlockMath math={`F_{wRd1}=0,6 \\cdot A_{MB} \\cdot \\dfrac{f_y}{1,1}  `} />
                    <BlockMath math={`A_{MB}=b \\cdot L = ${data.b / 10} \\cdot ${data.l / 10}`} />
                    <BlockMath math={`A_{MB}= ${result.amb} \\, cm^2`} />
                    <BlockMath math={`F_{wRd1}=0,6 \\cdot ${result.amb} \\cdot \\dfrac{${data.fy / 10}}{1,1}  `} />
                    <BlockMath math={`F_{wRd1} = ${result.fwrd1.toFixed(2)} \\, kN`} />
                  </MathDiv>
                  <h3>Verificação do metal de solda</h3>
                  <MathDiv>
                    <BlockMath math={`F_{wRd2}=0,6 \\cdot A_w \\cdot \\dfrac{f_w}{\\gamma_{w2}}  `} />
                    <BlockMath math={`a = 0,7 \\cdot b= 0,7 \\cdot ${data.b} \\, mm`} />
                    <BlockMath math={`a = ${result.a.toFixed(2)} \\, cm`} />
                    <BlockMath
                      math={`A_w = a \\cdot L = ${result.a.toFixed(2)} \\cdot ${data.l / 10} = ${result.aw.toFixed(2)} \\, cm^2`}
                    />
                    <BlockMath
                      math={`F_{wRd2}=0,6 \\cdot ${result.aw.toFixed(2)} \\cdot \\dfrac{${data.fw}}{ ${data.gammaw2} }  `}
                    />
                    <BlockMath math={`F_{wRd2}= ${result.fwrd2.toFixed(2)} \\, kN `} />
                  </MathDiv>
                  <h3>Conclusão</h3>
                  <MathDiv>
                    <BlockMath math={`\\boxed{F_{wRd}= ${result.fwrd.toFixed(2)} \\, kN}`} />
                  </MathDiv>
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
